<template>
    <transition name="panel-fade">
        <div v-if="show" class="sub-overlay" @click.self="close">
            <transition name="panel-slide">
                <aside v-if="show" class="sub-panel">
                    <div class="sub-panel-head">
                        <div>
                            <h2>Subscription</h2>
                            <p>Assign or change the subscription plan for {{ company?.name }}</p>
                        </div>
                        <button class="sub-close" @click="close">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>

                    <div class="sub-panel-body">
                        <div v-if="loadingCurrent" class="sub-loading">Loading…</div>

                        <template v-else>
                            <!-- Current plan -->
                            <div class="sub-section">
                                <label class="sub-label">Current Plan</label>
                                <div class="current-plan-box">
                                    <div class="current-plan-name">{{ company?.plan?.name || 'No plan assigned' }}</div>
                                    <span v-if="company?.plan" class="status-badge" :class="statusClass">
                                        {{ statusLabel }}
                                    </span>
                                </div>
                                <p v-if="company?.plan_expiry_date" class="expiry-line">
                                    Expires: {{ formatDate(company.plan_expiry_date) }}
                                </p>
                            </div>

                            <!-- Assign / change plan -->
                            <div class="sub-section">
                                <label class="sub-label">Select Plan</label>
                                <select v-model="selectedPlanId" class="form-input-premium">
                                    <option :value="null" disabled>Choose a plan...</option>
                                    <option v-for="plan in activePlans" :key="plan.id" :value="plan.id">
                                        {{ plan.name }} — ₹{{ formatPrice(plan.price) }} / {{ formatDuration(plan.duration_days) }}
                                    </option>
                                </select>
                                <p class="field-hint">Assigning a plan sets the expiry to today + the plan's duration.</p>

                                <button class="btn-assign" :disabled="!selectedPlanId || assigning" @click="assignPlan">
                                    <span v-if="assigning" class="spinner-small"></span>
                                    {{ assigning ? 'Assigning...' : (company?.plan ? 'Change Plan' : 'Assign Plan') }}
                                </button>
                            </div>

                            <!-- Extend -->
                            <div v-if="company?.plan" class="sub-section">
                                <label class="sub-label">Extend Current Plan</label>
                                <div class="extend-row">
                                    <input type="number" v-model.number="extendDays" min="1" placeholder="e.g., 7" class="form-input-premium">
                                    <button class="btn-extend" :disabled="!extendDays || extendDays <= 0 || extending" @click="extendPlan">
                                        <span v-if="extending" class="spinner-small"></span>
                                        {{ extending ? 'Extending...' : 'Extend' }}
                                    </button>
                                </div>
                                <p class="field-hint">Adds extra days on top of the current expiry date — the base date never resets.</p>
                            </div>

                            <div v-if="message" class="sub-message" :class="messageType">{{ message }}</div>

                            <!-- History -->
                            <div v-if="history.length" class="sub-section">
                                <label class="sub-label">History</label>
                                <ul class="history-list">
                                    <li v-for="h in history" :key="h.id" class="history-item">
                                        <span class="history-action" :class="h.action.toLowerCase()">{{ h.action }}</span>
                                        <div class="history-body">
                                            <p>{{ h.plan_name || '—' }}<template v-if="h.extended_by_days"> · +{{ h.extended_by_days }}d</template></p>
                                            <span>{{ formatDate(h.created_date) }}</span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </div>
                </aside>
            </transition>
        </div>
    </transition>
</template>

<script>
import axios from 'axios';

export default {
    name: 'SubscriptionPanel',
    props: {
        show: { type: Boolean, default: false },
        company: { type: Object, default: null },
    },
    emits: ['close', 'updated'],
    data() {
        return {
            plans: [],
            selectedPlanId: null,
            extendDays: null,
            assigning: false,
            extending: false,
            loadingCurrent: false,
            message: '',
            messageType: 'success',
            history: [],
        }
    },
    computed: {
        activePlans() {
            return this.plans.filter(p => p.is_active);
        },
        statusClass() {
            const s = this.company?.plan_status;
            if (s === 'ACTIVE') return 'active';
            if (s === 'EXPIRING_SOON') return 'expiring';
            if (s === 'EXPIRED') return 'expired';
            return '';
        },
        statusLabel() {
            const s = this.company?.plan_status;
            if (s === 'ACTIVE') return 'Active';
            if (s === 'EXPIRING_SOON') return 'Expiring Soon';
            if (s === 'EXPIRED') return 'Expired';
            return '—';
        }
    },
    watch: {
        show(val) {
            if (val) {
                this.message = '';
                this.selectedPlanId = null;
                this.extendDays = null;
                this.fetchPlans();
                this.fetchHistory();
            }
        }
    },
    methods: {
        authHeaders() {
            return { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` };
        },
        close() {
            this.$emit('close');
        },
        async fetchPlans() {
            this.loadingCurrent = true;
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/plans?page_size=100`, {
                    headers: this.authHeaders()
                });
                this.plans = response.data.items || [];
            } catch (error) {
                console.error('Error fetching plans:', error);
            } finally {
                this.loadingCurrent = false;
            }
        },
        async fetchHistory() {
            if (!this.company?.id) return;
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/company/${this.company.id}/plan/history`, {
                    headers: this.authHeaders()
                });
                this.history = response.data || [];
            } catch (error) {
                console.error('Error fetching plan history:', error);
            }
        },
        formatPrice(price) {
            return (price || 0).toLocaleString('en-IN');
        },
        formatDuration(days) {
            if (days === 30) return 'month';
            if (days === 90) return 'quarter';
            if (days === 365) return 'year';
            return `${days} days`;
        },
        formatDate(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        async assignPlan() {
            if (!this.selectedPlanId || this.assigning) return;
            this.assigning = true;
            this.message = '';
            try {
                await axios.patch(`${this.$API_URL}/super_admin/company/${this.company.id}/plan?plan_id=${this.selectedPlanId}`, {}, {
                    headers: this.authHeaders()
                });
                this.messageType = 'success';
                this.message = 'Plan assigned successfully';
                this.selectedPlanId = null;
                await this.fetchHistory();
                this.$emit('updated');
            } catch (error) {
                this.messageType = 'error';
                this.message = error.response?.data?.detail || 'Failed to assign plan';
            } finally {
                this.assigning = false;
            }
        },
        async extendPlan() {
            if (!this.extendDays || this.extendDays <= 0 || this.extending) return;
            this.extending = true;
            this.message = '';
            try {
                await axios.patch(`${this.$API_URL}/super_admin/company/${this.company.id}/plan/extend?days=${this.extendDays}`, {}, {
                    headers: this.authHeaders()
                });
                this.messageType = 'success';
                this.message = `Plan extended by ${this.extendDays} day(s)`;
                this.extendDays = null;
                await this.fetchHistory();
                this.$emit('updated');
            } catch (error) {
                this.messageType = 'error';
                this.message = error.response?.data?.detail || 'Failed to extend plan';
            } finally {
                this.extending = false;
            }
        }
    }
}
</script>

<style scoped>
.sub-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(2px);
    z-index: 1200;
    display: flex;
    justify-content: flex-end;
}

.sub-panel {
    width: 100%;
    max-width: 420px;
    height: 100%;
    background: white;
    box-shadow: -10px 0 40px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    font-family: var(--font-sans);
}

.sub-panel-head {
    padding: 1.75rem 1.75rem 1.25rem;
    border-bottom: 1px solid #eef2f7;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
}

.sub-panel-head h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text-primary);
}

.sub-panel-head p {
    margin: 0.35rem 0 0;
    font-size: 0.85rem;
    color: var(--text-secondary);
}

.sub-close {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.sub-close:hover { background: #f1f5f9; color: #ef4444; border-color: #fecaca; }

.sub-panel-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.75rem;
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
}

.sub-loading {
    text-align: center;
    color: #94a3b8;
    padding: 2rem 0;
}

.sub-section { display: flex; flex-direction: column; gap: 0.6rem; }

.sub-label {
    font-size: var(--fs-label);
    font-weight: var(--fw-label);
    letter-spacing: var(--tracking-label);
    text-transform: uppercase;
    color: var(--text-label);
}

.current-plan-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 0.9rem 1.1rem;
}

.current-plan-name {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
}

.status-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
}

.status-badge.active { background: rgba(16, 185, 129, 0.12); color: #10b981; }
.status-badge.expiring { background: rgba(245, 158, 11, 0.12); color: #d97706; }
.status-badge.expired { background: rgba(239, 68, 68, 0.12); color: #ef4444; }

.expiry-line {
    margin: 0;
    font-size: 0.8rem;
    color: var(--text-secondary);
    font-weight: 500;
}

.form-input-premium {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.9rem;
    font-family: var(--font-sans);
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
    background: white;
    color: var(--text-primary);
}

.form-input-premium:focus {
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
}

.field-hint {
    margin: 0;
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 500;
}

.btn-assign {
    margin-top: 0.25rem;
    padding: 0.8rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-assign:hover:not(:disabled) { transform: translateY(-1px); }
.btn-assign:disabled { opacity: 0.5; cursor: not-allowed; }

.extend-row {
    display: flex;
    gap: 0.6rem;
}

.extend-row .form-input-premium { flex: 1; }

.btn-extend {
    padding: 0 1.3rem;
    border: none;
    border-radius: 12px;
    background: #10b981;
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}

.btn-extend:hover:not(:disabled) { background: #059669; }
.btn-extend:disabled { opacity: 0.5; cursor: not-allowed; }

.sub-message {
    padding: 0.75rem 1rem;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
}

.sub-message.success { background: #dcfce7; color: #166534; }
.sub-message.error { background: #fee2e2; color: #991b1b; }

.history-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 220px;
    overflow-y: auto;
}

.history-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0.6rem 0;
    border-bottom: 1px solid #f1f5f9;
}

.history-action {
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    padding: 3px 8px;
    border-radius: 6px;
    flex-shrink: 0;
    background: #f1f5f9;
    color: #64748b;
}

.history-action.assigned { background: rgba(0, 113, 197, 0.1); color: #0071C5; }
.history-action.changed { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
.history-action.extended { background: rgba(16, 185, 129, 0.1); color: #10b981; }

.history-body { flex: 1; min-width: 0; }
.history-body p { margin: 0; font-size: 0.82rem; font-weight: 600; color: var(--text-primary); }
.history-body span { font-size: 0.7rem; color: #94a3b8; }

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.panel-fade-enter-active, .panel-fade-leave-active { transition: opacity 0.25s ease; }
.panel-fade-enter-from, .panel-fade-leave-to { opacity: 0; }

.panel-slide-enter-active { transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1); }
.panel-slide-leave-active { transition: transform 0.25s ease; }
.panel-slide-enter-from, .panel-slide-leave-to { transform: translateX(100%); }
</style>
